// ExecAcademic.js — "ผลการเรียน" (pos 90/99)
//
// คำถามของหน้านี้: ระดับไหนอ่อน วิชาไหนตกเยอะผิดปกติ และครูส่งเกรดทันกำหนดไหม
//
// หน้านี้มีสองโหมดโดยตั้งใจ ระหว่างภาคกับหลังปิดภาค — tb_grade ถูกใช้เก็บคะแนนเก็บ
// สะสมระหว่างภาค พอเอามาคิด GPA จะได้ 0.03 ทั้งโรงเรียน ซึ่งไม่ได้แปลว่านักเรียน
// ตกหมด แต่แปลว่ายังไม่ถึงเวลาตัดเกรด หน้าจึงต้องบอกให้ชัดแทนที่จะโชว์ตัวเลขนั้น
import React, { useMemo, useState } from "react";
import { Badge, Col, Form, ProgressBar, Row, Table } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import {
  ExecPage, Panel, SectionTitle, StatCard, EmptyState, ScrollTable,
  YearPicker, useExecYear, useExecData,
  fmtNum, fmtPct, toNum,
} from "./ExecShell";

const BAND_LABELS = {
  excellent: "ดีเยี่ยม (3.5+)",
  very_good: "ดีมาก (3.0-3.4)",
  good: "ดี (2.5-2.9)",
  fair: "พอใช้ (2.0-2.4)",
  poor: "ควรปรับปรุง (1.5-1.9)",
  very_poor: "ต้องแก้ไข (ต่ำกว่า 1.5)",
};
const BAND_ORDER = ["excellent", "very_good", "good", "fair", "poor", "very_poor"];
const BAND_COLORS = ["#198754", "#20c997", "#0dcaf0", "#ffc107", "#fd7e14", "#dc3545"];

const shortClass = (name) => String(name || "").replace("ชั้นมัธยมศึกษาปีที่ ", "ม.");

export default function ExecAcademic() {
  const { years, yearId, setYearId } = useExecYear();
  const [semester, setSemester] = useState("");
  const query = useMemo(() => ({ year_id: yearId, semester }), [yearId, semester]);
  const { data, loading, error } = useExecData("/api/executive/stats/academic", query);

  const readiness = data?.readiness || { looksFinal: false };
  const gpaByClass = useMemo(() => (Array.isArray(data?.gpaByClass) ? data.gpaByClass : []), [data]);
  const previousGpa = useMemo(() => (Array.isArray(data?.previousGpa) ? data.previousGpa : []), [data]);
  const gradeDistribution = useMemo(() => (data?.gradeDistribution || { byGrade: [] }), [data]);
  const failingSubjects = Array.isArray(data?.failingSubjects) ? data.failingSubjects : [];
  const roomGpa = data?.roomGpa || { top: [], bottom: [] };
  const submission = data?.submission || {};
  const islamic = data?.islamic || { available: false, rows: [] };
  const por5 = data?.por5 || { available: false };

  // ปีที่ตัดเกรดแล้วใช้ตัวเลขปีนี้ ปีที่ยังไม่ตัดใช้ปีก่อนเป็นตัวอ้างอิงแทน
  const displayGpa = readiness.looksFinal ? gpaByClass : previousGpa;
  const displayIsPrevious = !readiness.looksFinal && previousGpa.length > 0;

  const gpaChartOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#0d6efd"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "48%" } },
    dataLabels: { enabled: true, formatter: (v) => (v ? v.toFixed(2) : ""), style: { fontSize: "10px" } },
    legend: { show: false },
    xaxis: { categories: displayGpa.map((r) => shortClass(r.c_class)), labels: { style: { fontSize: "11px" } } },
    yaxis: { min: 0, max: 4, labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
  }), [displayGpa]);

  const distributionOptions = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors: ["#0d6efd"],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "52%", distributed: true } },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: gradeDistribution.byGrade.map((g) => g.label),
      labels: { style: { fontSize: "11px" } },
    },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
  }), [gradeDistribution]);

  const totalWeak = displayGpa.reduce((sum, r) => sum + toNum(r.weak), 0);
  const totalGraded = displayGpa.reduce((sum, r) => sum + toNum(r.graded_students), 0);
  const avgGpa = totalGraded > 0
    ? displayGpa.reduce((sum, r) => sum + toNum(r.avg_gpa) * toNum(r.graded_students), 0) / totalGraded
    : null;

  return (
    <ExecPage
      title="ผลการเรียน"
      icon="ri-award-line"
      lead="ผลการเรียนรายระดับชั้น วิชาที่ตกเยอะ และความคืบหน้าการบันทึกคะแนนของครู"
      activeYear={data?.year}
      loading={loading}
      error={error}
      toolbar={
        <>
          <Form.Select
            size="sm" style={{ width: "auto" }}
            value={semester} onChange={(e) => setSemester(e.target.value)}
          >
            <option value="">ทั้งปี</option>
            <option value="1">ภาคเรียนที่ 1</option>
            <option value="2">ภาคเรียนที่ 2</option>
          </Form.Select>
          <YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />
        </>
      }
    >
      {!readiness.looksFinal && (
        <div className="alert alert-warning d-flex gap-3 align-items-start mb-4">
          <i className="ri-information-line" style={{ fontSize: 22 }} />
          <div>
            <strong>ปีการศึกษานี้ยังไม่ได้ตัดเกรด</strong>
            <div className="fs-sm mt-1">
              ตอนนี้ครูกำลังทยอยบันทึกคะแนนเก็บลงในระบบ ({fmtNum(readiness.entered)} รายการ
              โดยมีเพียง {fmtPct(readiness.passShare)} ที่ถึงเกณฑ์ผ่าน) การนำตัวเลขระหว่างภาคมาคิด GPA
              จะได้ค่าที่ต่ำผิดจริงทั้งโรงเรียน หน้านี้จึงแสดงความคืบหน้าการบันทึกเป็นหลัก
              {displayIsPrevious && ` ส่วนผลการเรียนที่แสดงด้านล่างเป็นของปีการศึกษา ${data?.previousYear?.year_year} เพื่อใช้อ้างอิง`}
            </div>
          </div>
        </div>
      )}

      <SectionTitle
        icon="ri-edit-2-line"
        title="ความคืบหน้าการบันทึกของครู"
        note={`ปีการศึกษา ${data?.year?.year_year || "-"}${semester ? ` ภาคเรียนที่ ${semester}` : ""}`}
        color="warning"
      />
      <Row className="g-3 mb-4">
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-file-list-3-fill"
            color={toNum(submission.grade?.completePercent) >= 80 ? "success" : "warning"}
            label="วิชาที่ส่งเกรดครบ"
            value={fmtPct(submission.grade?.completePercent)}
            sub={`${fmtNum(submission.grade?.complete)} จาก ${fmtNum(submission.grade?.total)} วิชา-ห้อง`}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-close-circle-fill"
            color={toNum(submission.grade?.none) > 0 ? "danger" : "secondary"}
            label="ยังไม่เริ่มกรอกเลย"
            value={fmtNum(submission.grade?.none)}
            sub="วิชา-ห้องที่ยังไม่มีเกรดสักคน"
            tone={toNum(submission.grade?.none) > 0 ? "danger" : undefined}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-task-fill"
            color={toNum(submission.scoreItems?.withItemsPercent) >= 80 ? "success" : "warning"}
            label="วิชาที่ตั้งคะแนนเก็บแล้ว"
            value={fmtPct(submission.scoreItems?.withItemsPercent)}
            sub={`ยังไม่ตั้ง ${fmtNum(submission.scoreItems?.withoutItems)} วิชา-ห้อง · รวม ${fmtNum(submission.scoreItems?.itemCount)} ชิ้นงาน`}
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatCard
            icon="ri-user-heart-fill"
            color={toNum(por5.completePercent) >= 80 ? "success" : "warning"}
            label="ประเมินคุณลักษณะครบ"
            value={por5.available ? fmtPct(por5.completePercent) : "—"}
            sub={por5.available
              ? `${fmtNum(por5.complete)} จาก ${fmtNum(por5.total)} วิชา-ห้อง`
              : "ยังไม่มีข้อมูล ปพ.5"}
          />
        </Col>
      </Row>

      {toNum(submission.pendingWork?.students) > 0 && (
        <Row className="g-3 mb-4">
          <Col xs={12}>
            <Panel note="วิชาที่ยังไม่ตั้งคะแนนเก็บจะไม่ปรากฏในระบบติดตามงานค้างของครูประจำชั้นเลยทั้งห้อง">
              <div className="d-flex flex-wrap gap-4 align-items-center">
                <div>
                  <div className="fs-3 fw-semibold text-warning">{fmtNum(submission.pendingWork.students)}</div>
                  <div className="fs-sm text-secondary">นักเรียนที่มีงานค้างส่ง</div>
                </div>
                <div className="border-start ps-4">
                  <div className="fs-4 fw-semibold">{fmtNum(submission.pendingWork.pairs)}</div>
                  <div className="fs-sm text-secondary">รายการค้าง นับเป็นคู่นักเรียนกับวิชา</div>
                </div>
                <div className="border-start ps-4">
                  <div className="fs-4 fw-semibold">{fmtNum(submission.scoreItems?.withoutItems)}</div>
                  <div className="fs-sm text-secondary">วิชา-ห้องที่ครูยังไม่ตั้งคะแนนเก็บ</div>
                </div>
              </div>
            </Panel>
          </Col>
        </Row>
      )}

      {displayGpa.length > 0 && (
        <>
          <SectionTitle
            icon="ri-bar-chart-box-line"
            title={displayIsPrevious ? `ผลการเรียน ปีการศึกษา ${data?.previousYear?.year_year}` : "ผลการเรียน"}
            note={displayIsPrevious ? "ข้อมูลอ้างอิงจากปีที่ตัดเกรดเรียบร้อยแล้ว" : ""}
            color="primary"
          />
          <Row className="g-3 mb-4">
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-medal-fill" color="primary" label="GPA เฉลี่ยทั้งโรงเรียน"
                value={avgGpa === null ? "—" : avgGpa.toFixed(2)}
                sub={`จากนักเรียน ${fmtNum(totalGraded)} คน`}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-alert-fill"
                color={toNum(totalWeak) > 0 ? "danger" : "success"}
                label="GPA ต่ำกว่า 2.0"
                value={fmtNum(totalWeak)}
                sub={`คิดเป็น ${fmtPct(totalGraded > 0 ? Math.round((totalWeak / totalGraded) * 1000) / 10 : null)}`}
                tone={toNum(totalWeak) > 0 ? "danger" : undefined}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-time-fill" color="warning" label="ติด ร"
                value={fmtNum(gradeDistribution.incomplete)}
                sub="รอการแก้ไขผลการเรียน"
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-book-mark-fill" color="info" label="วิชาที่ตัดเกรดแล้ว"
                value={fmtNum(gradeDistribution.total)}
                sub="นับเป็นรายวิชาต่อนักเรียน"
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xl={7}>
              <Panel title="GPA เฉลี่ยรายระดับชั้น">
                <ReactApexChart
                  options={gpaChartOptions}
                  series={[{ name: "GPA", data: displayGpa.map((r) => toNum(r.avg_gpa)) }]}
                  type="bar" height={280}
                />
              </Panel>
            </Col>
            <Col xl={5}>
              <Panel title="การกระจายเกรด" note="นับเป็นรายวิชาต่อนักเรียน">
                {gradeDistribution.byGrade.length === 0 ? <EmptyState compact /> : (
                  <ReactApexChart
                    options={distributionOptions}
                    series={[{ name: "จำนวน", data: gradeDistribution.byGrade.map((g) => toNum(g.total)) }]}
                    type="bar" height={280}
                  />
                )}
              </Panel>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xl={7}>
              <Panel title="ผลการเรียนรายระดับชั้น" note="แยกตามช่วง GPA">
                <ScrollTable>
                  <Table className="table-sm mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>ชั้น</th>
                        <th className="text-end">นักเรียน</th>
                        <th className="text-end">GPA</th>
                        <th style={{ minWidth: 150 }}>การกระจาย</th>
                        <th className="text-end">ต่ำกว่า 2.0</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayGpa.map((row) => (
                        <tr key={row.c_id}>
                          <td className="fw-semibold">{shortClass(row.c_class)}</td>
                          <td className="text-end">{fmtNum(row.graded_students)}</td>
                          <td className="text-end fw-semibold">{row.avg_gpa === null ? "—" : row.avg_gpa.toFixed(2)}</td>
                          <td>
                            <div className="d-flex rounded overflow-hidden" style={{ height: 10 }}>
                              {BAND_ORDER.map((band, index) => {
                                const value = toNum(row.bands?.[band]);
                                if (value <= 0) return null;
                                return (
                                  <div
                                    key={band}
                                    title={`${BAND_LABELS[band]}: ${fmtNum(value)}`}
                                    style={{ width: `${(value / Math.max(row.graded_students, 1)) * 100}%`, background: BAND_COLORS[index] }}
                                  />
                                );
                              })}
                            </div>
                          </td>
                          <td className="text-end">
                            <Badge bg={toNum(row.weakPercent) >= 20 ? "danger" : toNum(row.weakPercent) >= 10 ? "warning" : "secondary"}>
                              {fmtNum(row.weak)} · {fmtPct(row.weakPercent)}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </ScrollTable>
                <div className="d-flex gap-3 flex-wrap fs-xs text-secondary mt-3">
                  {BAND_ORDER.map((band, index) => (
                    <span key={band} className="d-flex align-items-center gap-1">
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: BAND_COLORS[index], display: "inline-block" }} />
                      {BAND_LABELS[band]}
                    </span>
                  ))}
                </div>
              </Panel>
            </Col>
            <Col xl={5}>
              <Panel title="ห้องที่ GPA ต่ำสุด" note="เฉพาะห้องที่มีนักเรียนตั้งแต่ 10 คนขึ้นไป">
                {(roomGpa.bottom || []).length === 0 ? <EmptyState compact /> : roomGpa.bottom.map((room) => (
                  <div key={room.r_id} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                    <span>{room.room_label}<span className="text-secondary fs-xs ms-2">{fmtNum(room.students)} คน</span></span>
                    <Badge bg={toNum(room.avg_gpa) < 2 ? "danger" : "warning"}>{room.avg_gpa?.toFixed(2)}</Badge>
                  </div>
                ))}
                <h6 className="fs-sm mt-4 mb-2 text-secondary">ห้องที่ GPA สูงสุด</h6>
                {(roomGpa.top || []).map((room) => (
                  <div key={room.r_id} className="d-flex align-items-center justify-content-between border-bottom py-2 fs-sm">
                    <span>{room.room_label}<span className="text-secondary fs-xs ms-2">{fmtNum(room.students)} คน</span></span>
                    <Badge bg="success">{room.avg_gpa?.toFixed(2)}</Badge>
                  </div>
                ))}
              </Panel>
            </Col>
          </Row>

          {failingSubjects.length > 0 && (
            <>
              <SectionTitle
                icon="ri-error-warning-line"
                title="วิชาที่ผลการเรียนต่ำผิดปกติ"
                note="คอลัมน์ห้องคือหัวใจ วิชาที่ตกทุกห้องเป็นเรื่องหลักสูตร ส่วนตกห้องเดียวเป็นเรื่องการสอนห้องนั้น"
                color="danger"
              />
              <Row className="g-3 mb-4">
                <Col xs={12}>
                  <Panel note="นับเฉพาะวิชาที่ตัดเกรดแล้วอย่างน้อย 20 รายการ">
                    <ScrollTable>
                      <Table className="table-sm mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>รหัส</th>
                            <th>วิชา</th>
                            <th>ครูผู้สอน</th>
                            <th className="text-end">ห้อง</th>
                            <th className="text-end">ตัดเกรด</th>
                            <th className="text-end">ตก</th>
                            <th className="text-end">ติด ร</th>
                            <th className="text-end">อัตรา</th>
                          </tr>
                        </thead>
                        <tbody>
                          {failingSubjects.map((row) => (
                            <tr key={row.sub_id}>
                              <td className="fs-xs text-secondary">{row.sub_subid}</td>
                              <td className="fw-semibold">{row.sub_subname}</td>
                              <td className="fs-xs text-secondary text-truncate" style={{ maxWidth: 180 }}>
                                {row.teacher_names || "ไม่ระบุ"}
                              </td>
                              <td className="text-end">{fmtNum(row.rooms)}</td>
                              <td className="text-end">{fmtNum(row.graded)}</td>
                              <td className="text-end text-danger">{fmtNum(row.failed)}</td>
                              <td className="text-end text-warning">{fmtNum(row.incomplete)}</td>
                              <td className="text-end">
                                <Badge bg={toNum(row.failRate) >= 30 ? "danger" : toNum(row.failRate) >= 15 ? "warning" : "secondary"}>
                                  {fmtPct(row.failRate)}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </ScrollTable>
                  </Panel>
                </Col>
              </Row>
            </>
          )}
        </>
      )}

      <SectionTitle icon="ri-moon-clear-line" title="ภาคศาสนา" note="คะแนนรวมปลายภาค ไม่มีคะแนนเก็บรายชิ้น จึงไม่มี GPA" color="success" />
      <Row className="g-3 mb-4">
        <Col xs={12}>
          <Panel note={islamic.available ? `คะแนนเต็ม ${fmtNum(islamic.fullMark)}` : ""}>
            {!islamic.available || islamic.rows.length === 0 ? (
              <EmptyState message="ยังไม่มีการบันทึกคะแนนภาคศาสนาในปีนี้" />
            ) : (
              <ScrollTable>
                <Table className="table-sm mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>ชั้น</th>
                      <th className="text-end">บันทึกแล้ว</th>
                      <th className="text-end">คะแนนเฉลี่ย</th>
                      <th style={{ minWidth: 130 }}>เทียบคะแนนเต็ม</th>
                      <th className="text-end">ต่ำกว่าครึ่ง</th>
                    </tr>
                  </thead>
                  <tbody>
                    {islamic.rows.map((row) => (
                      <tr key={row.ic_id}>
                        <td className="fw-semibold">{row.ic_class}</td>
                        <td className="text-end">{fmtNum(row.scored)}</td>
                        <td className="text-end fw-semibold">{row.avg_score ?? "—"}</td>
                        <td>
                          <ProgressBar
                            style={{ height: 7 }}
                            now={islamic.fullMark > 0 ? (toNum(row.avg_score) / islamic.fullMark) * 100 : 0}
                            variant={toNum(row.avg_score) >= islamic.fullMark * 0.7 ? "success" : toNum(row.avg_score) >= islamic.fullMark * 0.5 ? "warning" : "danger"}
                          />
                        </td>
                        <td className="text-end">
                          <Badge bg={toNum(row.belowHalfPercent) >= 30 ? "danger" : "secondary"}>
                            {fmtNum(row.below_half)} · {fmtPct(row.belowHalfPercent)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ScrollTable>
            )}
          </Panel>
        </Col>
      </Row>
    </ExecPage>
  );
}
